import React, { useEffect, useId, useRef, useState } from "react";
import { ReactComponent as OutlookIcon } from "assets/svgs/outlook.svg";
import { ReactComponent as GoogleIcon } from "assets/svgs/google.svg";
import { ReactComponent as AppleIcon } from "assets/svgs/apple.svg";
import { ReactComponent as CalendarIcon } from "assets/svgs/calendar.svg";
import { buildCalendarLinks, buildICS } from "utils/ics";

const sanitizeFilename = (s) => (s || "event").replace(/[^a-z0-9\-_. ]/gi, "_");

const Divider = () => <div className="h-px w-full bg-slate-200" />;

const MenuItem = React.forwardRef(function MenuItem(
	{ icon, label, onClick },
	ref
) {
	return (
		<button
			ref={ref}
			role="menuitem"
			onClick={onClick}
			className="save-to-calendar-option"
		>
			{icon}
			<span>{label}</span>
		</button>
	);
});

const SaveToCalendar = ({
	title,
	description = "",
	location = "",
	label,
	start,
	end,
	timezone,
}) => {
	const btnId = useId();
	const menuId = useId();
	const [open, setOpen] = useState(false);
	const rootRef = useRef(null);
	const firstItemRef = useRef(null);

	useEffect(() => {
		const onDocClick = (e) => {
			if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
		};

		const  onEsc = (e) => {
			if (e.key === "Escape") setOpen(false);
		};

		document.addEventListener("mousedown", onDocClick);
		document.addEventListener("keydown", onEsc);

		return () => {
			document.removeEventListener("mousedown", onDocClick);
			document.removeEventListener("keydown", onEsc);
		};
	}, []);

	useEffect(() => {
		if (open && firstItemRef.current) firstItemRef.current.focus();
	}, [open]);

	const urls = buildCalendarLinks({ title, description, location, start, end });

	function handleIcsDownload() {
		const ics = buildICS({ title, description, location, start, end, timezone });
		const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `${sanitizeFilename(title || "event")}.ics`;
		document.body.appendChild(a);
		a.click();
		a.remove();
		URL.revokeObjectURL(url);
		setOpen(false);
	}

	return (
		<div ref={rootRef} className="save-to-calendar">
			<button
				id={btnId}
				type="button"
				aria-haspopup="menu"
				aria-expanded={open}
				aria-controls={menuId}
				onClick={() => setOpen(!open)}
				className="save-to-calendar-btn"
			>
				<CalendarIcon className="size-5" />
				{label}
			</button>

			{open && (
				<div
					id={menuId}
					role="menu"
					aria-labelledby={btnId}
					className="save-to-calendar-body"
				>
					<MenuItem
						ref={firstItemRef}
						onClick={() => {
							window.open(urls.google, "_blank", "noopener");
							setOpen(false);
						}}
						icon={<GoogleIcon className="size-5" />}
						label="Google Calendar"
					/>
					<Divider />
					<MenuItem
						onClick={handleIcsDownload}
						icon={<AppleIcon className="size-5" />}
						label="Apple Calendar"
					/>
					<Divider />
					<MenuItem
						onClick={() => {
							window.open(urls.outlook, "_blank", "noopener");
							setOpen(false);
						}}
						icon={<OutlookIcon className="size-5" />}
						label="Outlook.com"
					/>
				</div>
			)}
		</div>
	);
};

export default SaveToCalendar;